import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@ui/alert-dialog";
import { Input } from "@ui/input";
import { Controller, useForm } from "react-hook-form";
import TranslatedText from "@components/items/display/TranslatedText";
import { translate } from "@modules/localizations/translate";

const ConfirmDeleteAccountDialog = ({
  openDialog,
  setOpenDialog,
  setOpenRemoveAccountDialog,
}: {
  openDialog: boolean;
  setOpenDialog: (state: boolean) => void;
  setOpenRemoveAccountDialog: (state: boolean) => void;
}) => {
  type Input = {
    deleteStatment: string;
  };

  const {
    control,
    formState: { isDirty, errors },
    reset,
  } = useForm<Input>({ mode: "all", defaultValues: { deleteStatment: "" } });

  const isDisabled = !isDirty || !!errors?.deleteStatment;
  const onOpenChange = () => {
    setOpenDialog(false);
  };

  return (
    <AlertDialog
      open={openDialog}
      onOpenChange={(open) => {
        onOpenChange();
      }}
    >
      <AlertDialogContent className="border-none bg-white dark:bg-[#37383F]">
        <AlertDialogHeader>
          <AlertDialogTitle className="ltr:text-left rtl:text-right">
            <TranslatedText
              text={`settings.dialogs.${"delete account"}`}
              className="text-[20px] font-medium dark:text-white"
            />
          </AlertDialogTitle>
          <div className="flex flex-col items-start justify-center gap-3 pb-5 pt-2 text-sm ltr:text-left rtl:text-right">
            <TranslatedText
              text={`settings.dialogs.${"your profile data, account information, playback history, playlists, and any Logiez privileges will be completely deleted."}`}
              className="dark:text-white"
            />
            <TranslatedText
              text={`settings.dialogs.${"your account will be permanently deleted within 30 days. If you do not log in during that period, all of your information will be permanently removed and cannot be recovered in the future."}`}
              className="dark:text-white"
            />
            <div className="flex items-center justify-start gap-2">
              <TranslatedText
                text={`settings.dialogs.${"type"}`}
                className="dark:text-white"
              />
              <TranslatedText
                text={`settings.dialogs.${"delete my account"}`}
                className="font-medium dark:text-white"
              />
              <TranslatedText
                text={`settings.dialogs.${"confirm"}`}
                className="dark:text-white"
              />
            </div>
            <Controller
              name="deleteStatment"
              control={control}
              rules={{
                required: true,
                validate: (value) =>
                  value ===
                  translate(`settings.dialogs.${"delete my account"}`),
              }}
              render={({ field }) => (
                <Input
                  placeholder={translate(
                    `settings.dialogs.${"delete my account"}`
                  )}
                  className={`dark:bg-[#898A8D]/50 dark:text-white`}
                  {...field}
                />
              )}
            />
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-x-4">
          <AlertDialogAction
            className="bg-[#D9D9D9] hover:bg-[#D9D9D9] hover:opacity-85"
            onClick={() => {
              setOpenRemoveAccountDialog(true);
              reset();
            }}
            disabled={isDisabled}
          >
            <TranslatedText
              text={`settings.dialogs.${"confirm deletion"}`}
              className="text-[16px] font-normal !text-[#FF5153]"
            />
          </AlertDialogAction>
          <AlertDialogCancel className="dark:border-none dark:hover:bg-transparent">
            <TranslatedText
              text={`settings.dialogs.${"cancel the deletion request"}`}
              className="text-[16px] font-normal !text-[#FF5153]"
            />
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmDeleteAccountDialog;
